import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withStyles } from '@material-ui/core';
import { ThemeProvider } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DataTable from '../../shared/components/data-table/DataTable';
import StateBlock from '../../shared/components/data-table/StateBlock';
import { sortClientRows } from '../../shared/components/data-table/data-table.utils';
import superAdminTheme from '../../shared/components/data-table/superadmin-theme';
import MappingFormDialog from './MappingFormDialog';
import styles from './cap-mapping.style';
import {
  createCapMapping,
  deleteCapMapping,
  fetchCapClients,
  fetchCapMappings,
  updateCapMapping,
} from './services/cap-mappings.service';

const CONTROL_HEIGHT = 40;

const formatLocationCell = (location) => {
  if (!location) {
    return '-';
  }
  const code = location.shortCode ? ` (${location.shortCode})` : '';
  return `${location.locationName || 'Unnamed'}${code}`;
};

const CapMapping = ({ classes }) => {
  const [clients, setClients] = useState([]);
  const [clientName, setClientName] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState({ name: 'csvFacilityName', direction: 'asc' });
  const [dialog, setDialog] = useState({ mode: 'add', open: false, mapping: null });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  const loadClients = useCallback(async () => {
    try {
      const data = await fetchCapClients();
      const list = Array.isArray(data) ? data : [];
      setClients(list);
      setClientName((current) => current || list[0] || '');
    } catch (err) {
      console.error('[CapMapping] Failed to load clients', err);
      setLoadError(true);
    }
  }, []);

  const loadMappings = useCallback(async (selectedClient) => {
    if (!selectedClient) {
      setRows([]);
      return;
    }
    setLoading(true);
    setLoadError(false);
    try {
      const data = await fetchCapMappings(selectedClient);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[CapMapping] Failed to load mappings', err);
      setLoadError(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadMappings(clientName);
    setPage(1);
  }, [clientName, loadMappings]);

  const filteredRows = useMemo(() => {
    const needle = searchInput.trim().toLowerCase();
    if (!needle) {
      return rows;
    }
    return rows.filter((row) => {
      const haystack = [
        row.csvFacilityName,
        row.csvHuName,
        row.facilityLocation?.locationName,
        row.facilityLocation?.shortCode,
        row.huLocation?.locationName,
        row.huLocation?.shortCode,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [rows, searchInput]);

  const gridRows = useMemo(() => filteredRows.map((row) => ({ rowData: row })), [filteredRows]);

  const columns = useMemo(
    () => [
      { name: 'csvFacilityName', displayName: 'CSV facility', sortable: true },
      {
        name: 'csvHuName',
        displayName: 'CSV HU',
        sortable: true,
        render: (rowData) => rowData.csvHuName || '-',
      },
      {
        name: 'facilityLocation',
        displayName: 'Facility location',
        render: (rowData) => formatLocationCell(rowData.facilityLocation),
        sortValue: (rowData) => rowData.facilityLocation?.locationName || '',
      },
      {
        name: 'huLocation',
        displayName: 'HU location',
        render: (rowData) => formatLocationCell(rowData.huLocation),
        sortValue: (rowData) => rowData.huLocation?.locationName || '',
      },
      {
        name: 'actions',
        displayName: 'Actions',
        align: 'right',
        render: (rowData) => (
          <Box display="inline-flex" gap={0.5} justifyContent="flex-end">
            <IconButton
              aria-label="Edit mapping"
              size="small"
              onClick={() => setDialog({ mode: 'edit', open: true, mapping: rowData })}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton aria-label="Delete mapping" size="small" onClick={() => setDeleteTarget(rowData)}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [],
  );

  const sortedGridRows = useMemo(() => sortClientRows(gridRows, sort, columns), [gridRows, sort, columns]);

  const handleSave = async (body) => {
    setSaving(true);
    try {
      if (dialog.mode === 'edit' && dialog.mapping?.id != null) {
        await updateCapMapping(dialog.mapping.id, body);
        setToast({ message: 'Mapping updated', severity: 'success' });
      } else {
        await createCapMapping(body);
        setToast({ message: 'Mapping created', severity: 'success' });
      }
      setDialog({ mode: 'add', open: false, mapping: null });
      if (body.clientName !== clientName) {
        setClientName(body.clientName);
      } else {
        await loadMappings(clientName);
      }
    } catch (err) {
      setToast({ message: err?.message || 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) {
      return;
    }
    setDeleting(true);
    try {
      await deleteCapMapping(deleteTarget.id);
      setToast({ message: 'Mapping deleted', severity: 'success' });
      setDeleteTarget(null);
      await loadMappings(clientName);
    } catch (err) {
      setToast({ message: err?.message || 'Delete failed', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <ThemeProvider theme={superAdminTheme}>
      <Box className={classes.root} data-testid="cap-mapping-root">
        <Box>
          <Typography variant="h5">CAP Autoprovisioning Mappings</Typography>
          <Typography color="text.secondary" variant="body2">
            Map client CSV facility and housing-unit names to Orijin locations for existing CAP clients.
          </Typography>
        </Box>

        <Box className={classes.toolbar}>
          <TextField
            className={classes.clientSelect}
            label="Client"
            select
            size="small"
            value={clientName}
            onChange={(event) => setClientName(event.target.value)}
          >
            {clients.map((client) => (
              <MenuItem key={client} value={client}>
                {client}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            className={classes.searchField}
            placeholder="Search CSV names or location codes"
            size="small"
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton aria-label="Clear search" edge="end" size="small" onClick={() => setSearchInput('')}>
                    <ClearRoundedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { height: CONTROL_HEIGHT },
            }}
          />
          <Box className={classes.grow} />
          <Button
            startIcon={<AddRoundedIcon />}
            variant="contained"
            disabled={!clientName}
            onClick={() => setDialog({ mode: 'add', open: true, mapping: null })}
          >
            Add mapping
          </Button>
        </Box>

        {loadError ? (
          <StateBlock
            variant="error"
            title="Unable to load mappings"
            description="Refresh and try again. If the problem continues, check portal API access."
            onRetry={() => loadMappings(clientName)}
            data-testid="cap-mapping-error"
          />
        ) : (
          <DataTable
            columns={columns}
            rows={sortedGridRows}
            loading={loading}
            title="Mappings"
            subtitle={`${filteredRows.length} mapping${filteredRows.length === 1 ? '' : 's'}${
              clientName ? ` for ${clientName}` : ''
            }`}
            onRefresh={() => loadMappings(clientName)}
            paginationMode="client"
            page={page}
            pageSize={pageSize}
            pageSizeOptions={[25, 50, 100]}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            sort={sort}
            onSortChange={setSort}
            data-testid="cap-mapping-grid"
            emptyState={
              <StateBlock
                variant="empty"
                title="No mappings"
                description={
                  searchInput
                    ? 'No mappings match the current search.'
                    : 'Add a mapping to connect a CSV facility/HU to an Orijin location.'
                }
                data-testid="cap-mapping-empty"
              />
            }
          />
        )}

        <MappingFormDialog
          open={dialog.open}
          mode={dialog.mode}
          clients={clients}
          initialClientName={clientName}
          initialMapping={dialog.mapping}
          saving={saving}
          onClose={() => setDialog({ mode: 'add', open: false, mapping: null })}
          onSubmit={handleSave}
        />

        <Dialog open={Boolean(deleteTarget)} onClose={deleting ? undefined : () => setDeleteTarget(null)}>
          <DialogTitle>Delete mapping?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Remove the mapping for CSV facility &quot;{deleteTarget?.csvFacilityName}&quot;
              {deleteTarget?.csvHuName ? ` / HU "${deleteTarget.csvHuName}"` : ''}. The next CAP job run will use the
              updated mappings.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={Boolean(toast)}
          autoHideDuration={4000}
          onClose={() => setToast(null)}
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        >
          {toast ? (
            <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
              {toast.message}
            </Alert>
          ) : null}
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default withStyles(styles)(CapMapping);
